// FILE: environmentQueries.ts
// Purpose: The query keys everything about persistent environments is cached
//          under, so one invalidation reaches all of them.
// Layer: Client data access
//
// Environments change from three places the browser does not drive: the
// 10-minute sync sweep, an agent running `sync-base`, and another person
// creating or destroying one. All three end as `environment.*` domain events on
// the push channel, and `__root` turns those into a single
// `invalidateQueries({ queryKey: environmentQueryKeys.all })` — which is only
// possible if every environment query hangs off the same root.

import type { EnvironmentId, ProjectId } from "@taskin-code/contracts";

export const environmentQueryKeys = {
  all: ["environments"] as const,
  /** The environments of a project, as seen through one of the two routes. */
  list: (projectId: ProjectId | null, scope: "admin" | "usable") =>
    ["environments", "list", scope, projectId] as const,
  /** The roster that names the owners in the Personales table. */
  roster: () => ["environments", "roster"] as const,
  /** Freshness of one environment: state, distance and the missing commits. */
  syncState: (environmentId: EnvironmentId) =>
    ["environments", "sync-state", environmentId] as const,
  /** Database copies of one environment. */
  snapshots: (environmentId: EnvironmentId) =>
    ["environments", "snapshots", environmentId] as const,
  /**
   * The isolated previews of a project.
   *
   * Under the same root on purpose: previews are built and destroyed by an
   * agent or by the expiry sweep — never by whoever is looking at them — and
   * `pr-preview.*` domain events already invalidate this root in `__root`, so
   * the list refreshes with no poll of its own.
   */
  prPreviews: (projectId: ProjectId | null) => ["environments", "pr-previews", projectId] as const,
};
